import React, { useState, useEffect, useMemo } from 'react';
import { View, StyleSheet, ScrollView, Platform, RefreshControl } from 'react-native';
import { afterInteractions } from 'src/core/utils/afterInteractions';
import type { ViewStyle } from 'react-native';
import RemoteImage from 'src/presentation/components/molecules/RemoteImage';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import checkIsEven from 'src/core/utils/checkIsEven';
import NavBar from 'src/presentation/components/organisms/NavBar';
import Card from 'src/presentation/components/atoms/Card';
import Loader from 'src/presentation/components/atoms/Loader';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import EventDetailLink from 'src/presentation/components/molecules/EventDetailLink';
import MySubmissionBanner from 'src/presentation/components/molecules/MySubmissionBanner';
import Footer from 'src/presentation/components/organisms/Footer';
import { useFooterHeight } from 'src/presentation/hooks/useFooterHeight';
import { useContentWidth } from 'src/presentation/hooks/useContentWidth';
import { useEventStore } from 'src/domain/stores/useEventStore';
import { useNavigationStore } from 'src/domain/stores/useNavigationStore';
import { useAnalyticsStore } from 'src/domain/stores/useAnalyticsStore';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Dimens from 'src/core/utils/Dimens';
import Translations from 'src/core/utils/Translations';
import type Constants from 'src/core/utils/Constants';

function EventScreen() {
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenEvent>>();
	const eventStore = useEventStore();
	const navigationStore = useNavigationStore();
	const analyticsStore = useAnalyticsStore();
	const [title, setTitle] = useState('');
	const [refreshing, setRefreshing] = useState(false);
	const footerHeight = useFooterHeight();
	const contentWidth = useContentWidth();
	// EventScreen does not fetch on mount (the grid pre-fetches the event). Keep showing the loader
	// until the push animation has settled so a momentarily-empty store never flashes the empty state.
	const [settling, setSettling] = useState(true);

	const sizedStyles = useMemo(
		(): { eventLogoContainer: ViewStyle; linkContainer: ViewStyle } => ({
			eventLogoContainer: { width: contentWidth },
			linkContainer: { width: contentWidth },
		}),
		[contentWidth],
	);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		setTitle(route?.params?.title ?? '');
		analyticsStore.postEventOS(navigationStore.activeEventUuid ?? '');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		const task = afterInteractions(() => setSettling(false));
		return () => task.cancel?.();
	}, []);

	const handleRefresh = async () => {
		setRefreshing(true);
		await eventStore.fetchEvent(eventStore.activeEventUuid ?? '', true);
		setRefreshing(false);
	};

	const logoUrl = () => {
		if (eventStore.eventLogo && eventStore.eventLogo !== null && eventStore.eventLogo.startsWith('../') === false) {
			return eventStore.eventLogo;
		} else {
			return false;
		}
	};

	const renderEventDetailLinks = () => {
		const eventDetailLinks = eventStore.activeEventView.filter((event) => Number(event.hide_on_home) !== 1);

		return eventDetailLinks.map((page, index: number) => {
			return (
				<EventDetailLink
					data={page}
					key={page.id}
					spaceAround={{
						marginRight: checkIsEven(index) ? (Platform.OS === 'ios' ? 30 : 16) : 0,
					}}
					theme={eventStore.activeTheme}
				/>
			);
		});
	};

	const logo = logoUrl();

	return (
		<View style={{ flex: 1 }}>
			<NavBar
				backBtn={true}
				bgImage={eventStore.activeTheme.header_bg_image}
				bgcolor={eventStore.activeTheme.header_bg_color}
				color={eventStore.activeTheme.header_text_color}
				editBtn={false}
				statusBarStyle={eventStore.activeTheme.statusbar_color}
				title={title}
			/>

			<Card backgroundColor={eventStore.activeTheme.app_bg_color} backgroundImage={eventStore.activeTheme.app_bg_image}>
				<ScrollView
					contentContainerStyle={{ flexGrow: 1, paddingBottom: footerHeight }}
					refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
					style={{ flex: 1, width: '100%' }}
				>
					{eventStore.activeEventView.length === 0 ? (
						settling ? (
							<Loader />
						) : (
							<EmptyState
								icon="albums-outline"
								message={Translations.textNoContentMessage}
								title={Translations.textNoContentTitle}
							/>
						)
					) : (
						<View>
							<MySubmissionBanner />
							{logo ? (
								<View style={[styles.eventLogoContainer, sizedStyles.eventLogoContainer]}>
									<RemoteImage resizeMode="contain" style={styles.eventLogo} uri={String(logo)} />
								</View>
							) : null}
							<View style={[styles.linkContainer, sizedStyles.linkContainer]}>{renderEventDetailLinks()}</View>
						</View>
					)}
				</ScrollView>
			</Card>

			<Footer
				backgroundColor={eventStore.activeTheme.footer_bg_color}
				backgroundImage={eventStore.activeTheme.footer_bg_image}
				backgroundType={eventStore.activeTheme.footer_bg_type}
			/>
		</View>
	);
}

export default EventScreen;

const styles = StyleSheet.create({
	eventLogoContainer: {
		paddingLeft: Dimens.padding40,
		paddingRight: Dimens.padding40,
		height: 48,
		marginTop: Dimens.margin24,
		marginBottom: Dimens.margin12,
	},
	eventLogo: {
		width: '100%',
		height: 48,
	},
	linkContainer: {
		flexDirection: 'row',
		flexWrap: 'wrap',
		alignItems: 'flex-start',
		justifyContent: 'flex-start',
		paddingTop: 28,
		paddingLeft: Dimens.padding40,
		paddingRight: Dimens.padding40,
	},
});
